// Where the launcher sits on the host site, per device. Configured in the
// backoffice (widget configuration `style.position`); any missing value falls
// back to the historical default below, so a configuration saved before the
// field existed renders exactly as it always did.

export type LauncherSide = "left" | "right";

export interface LauncherPosition {
  side: LauncherSide;
  /** Distance of the launcher from the bottom edge, px. */
  bottom: number;
  /** Distance of the launcher from its side edge, px. */
  offset: number;
}

export const DEFAULT_LAUNCHER_POSITION: Record<
  "mobile" | "desktop",
  LauncherPosition
> = {
  mobile: { side: "right", bottom: 20, offset: 20 },
  desktop: { side: "right", bottom: 50, offset: 50 },
};

// Same bounds the backend clamps to (WidgetPosition.MaxBottom / MaxOffset).
const MAX_BOTTOM = 400;
const MAX_OFFSET = 300;

// The open desktop panel sits 20px from the corner when the launcher is at its
// default 50px, and moves by the same amount the launcher is moved.
const PANEL_INSET = 20;

function toPx(value: unknown, fallback: number, max: number): number {
  const n = typeof value === "string" ? Number(value) : value;
  if (typeof n !== "number" || !Number.isFinite(n)) return fallback;
  return Math.min(Math.max(Math.round(n), 0), max);
}

export function resolveLauncherPosition(
  position: any,
  isMobile: boolean
): LauncherPosition {
  const device = isMobile ? "mobile" : "desktop";
  const fallback = DEFAULT_LAUNCHER_POSITION[device];
  const raw = position?.[device];
  if (!raw || typeof raw !== "object") return fallback;
  return {
    side: raw.side === "left" ? "left" : "right",
    bottom: toPx(raw.bottom, fallback.bottom, MAX_BOTTOM),
    offset: toPx(raw.offset, fallback.offset, MAX_OFFSET),
  };
}

/** Anchor of the open desktop panel (and of the slider docked to it). */
export function resolvePanelAnchor(launcher: LauncherPosition): {
  side: LauncherSide;
  bottom: number;
  offset: number;
} {
  const base = DEFAULT_LAUNCHER_POSITION.desktop;
  return {
    side: launcher.side,
    bottom: Math.max(PANEL_INSET, PANEL_INSET + launcher.bottom - base.bottom),
    offset: Math.max(PANEL_INSET, PANEL_INSET + launcher.offset - base.offset),
  };
}
